/**
 * B6 · «Mi ruta» — el camino de niveles del alumno.
 *
 * La página entera se gatea por `academy.progression.path`: apagada, el alumno
 * vuelve a `/learn` con la lista de siempre y **no pierde nada** — el motor
 * sigue calculando el estado, los certificados siguen emitiéndose y el
 * expediente no depende del mapa.
 */
import { createMemo, createSignal, For, Show } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { Route as RouteIcon } from "lucide-solid";
import { withFeatureGate } from "@/features/flags/feature-gate";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { PillTabs } from "@/components/ui/pill-tabs";
import { PathMap } from "@/features/progression/path-map";
import { NextStageOffers } from "@/features/progression/next-stage-offer";
import { useTracks } from "@/features/progression/use-progression";
import { PROGRESSION_PATH_FEATURE } from "@/features/progression/api";
import { appRoute } from "./route";

function PathPage() {
  const { t } = useI18n();
  const tracks = useTracks();
  const [selected, setSelected] = createSignal<string | undefined>();

  const available = createMemo(() => tracks.data?.tracks ?? []);
  const activeTrackId = createMemo(() => selected() ?? available()[0]?.id);

  return (
    <PageContainer maxWidth="2xl">
      <PageHeader
        title={t(msg`My path`)}
        description={t(
          msg`Your levels, in order. Finish one to open the next — and see exactly what's missing when one is locked.`,
        )}
        icon={RouteIcon}
      />

      {/* La oferta va ARRIBA y persiste: no es un modal que se cierra y se
          pierde. Vive aquí y en /payments hasta que caduque o se descarte. */}
      <div class="mt-4">
        <NextStageOffers />
      </div>

      <Show when={available().length > 1}>
        <div class="mt-4">
          <PillTabs
            tabs={available().map((track) => ({ id: track.id, label: track.name }))}
            value={activeTrackId() ?? ""}
            onChange={(id) => setSelected(id)}
            testIdPrefix="progression-track"
            label={t(msg`Learning paths`)}
          />
        </div>
      </Show>

      <div class="mt-6">
        <Show when={activeTrackId()}>{(id) => <PathMap trackId={id()} />}</Show>
      </div>

      {/* Leyenda: la lectura del camino no puede depender de distinguir
          colores, así que los estados se enumeran con su icono y su nombre. */}
      <ul class="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <For
          each={[
            t(msg`Locked — something is missing`),
            t(msg`Open — you can start`),
            t(msg`In progress`),
            t(msg`Completed`),
            t(msg`Certified`),
          ]}
        >
          {(label) => <li>{label}</li>}
        </For>
      </ul>
    </PageContainer>
  );
}

export const pathRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/path",
  component: withFeatureGate(PROGRESSION_PATH_FEATURE, PathPage),
});
